import { useState } from 'react';
import { Card, CardBody, Input, Button } from '@img/ui';
import { api, ApiError } from '../lib/api';

export function ContactPage() {
  const [form, setForm] = useState({ name: '', phone: '', email: '', subject: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErrors({});
    try {
      await api.post('/api/messages', form);
      setSuccess(true);
      setForm({ name: '', phone: '', email: '', subject: '', message: '' });
    } catch (err) {
      if (err instanceof ApiError && err.fieldErrors) {
        const fe: Record<string, string> = {};
        Object.entries(err.fieldErrors).forEach(([k, v]) => fe[k] = v[0] ?? '');
        setErrors(fe);
      } else {
        setErrors({ _: (err as Error).message });
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-12">
      <div className="text-center mb-10">
        <h1 className="text-3xl font-black text-navy-dark mb-2">تواصل معنا</h1>
        <p className="text-gray-600">فريقنا جاهز للرد على استفساراتك في أي وقت</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Contact info */}
        <aside className="space-y-3">
          <ContactItem icon="📞" title="الهاتف" value="0114993666" href="tel:0114993666" />
          <ContactItem icon="📱" title="واتساب"  value="0114993666" href="https://wa.me/966114993666" />
          <ContactItem icon="📧" title="الإيميل" value="info@img.sa"  href="mailto:info@img.sa" />
          <ContactItem icon="🕐" title="ساعات العمل" value="السبت — الخميس 8 ص — 10 م" />
        </aside>

        {/* Form */}
        <Card className="md:col-span-2">
          <CardBody className="p-8">
            {success ? (
              <div className="text-center py-8">
                <div className="text-6xl mb-3">✅</div>
                <h2 className="text-xl font-black text-navy-dark mb-2">تم استلام رسالتك</h2>
                <p className="text-gray-600 mb-6">سنرد عليك في أقرب وقت ممكن.</p>
                <Button variant="primary" onClick={() => setSuccess(false)}>إرسال رسالة أخرى</Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="الاسم"  value={form.name}  onChange={handleChange('name')}  error={errors.name}  required />
                  <Input label="الجوال" value={form.phone} onChange={handleChange('phone')} error={errors.phone} required type="tel" dir="ltr" />
                </div>
                <Input label="البريد الإلكتروني" value={form.email} onChange={handleChange('email')} error={errors.email} type="email" />
                <Input label="الموضوع"           value={form.subject} onChange={handleChange('subject')} error={errors.subject} />
                <div>
                  <label className="block text-sm font-bold text-ink mb-1">الرسالة</label>
                  <textarea
                    value={form.message}
                    onChange={handleChange('message')}
                    rows={5}
                    required
                    className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white"
                  />
                  {errors.message && <p className="text-red-600 text-xs mt-1">{errors.message}</p>}
                </div>
                {errors._ && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg p-3">{errors._}</div>}
                <Button type="submit" variant="gold" size="lg" loading={busy} className="w-full">إرسال الرسالة</Button>
              </form>
            )}
          </CardBody>
        </Card>
      </div>
    </div>
  );
}

function ContactItem({ icon, title, value, href }: { icon: string; title: string; value: string; href?: string }) {
  const Inner = (
    <Card hover={!!href}>
      <CardBody className="flex items-center gap-3">
        <div className="text-3xl">{icon}</div>
        <div>
          <div className="text-xs text-gray-500">{title}</div>
          <div className="font-bold text-ink text-sm" dir="ltr">{value}</div>
        </div>
      </CardBody>
    </Card>
  );
  return href ? <a href={href} target="_blank" rel="noopener">{Inner}</a> : Inner;
}
